import { action, observable } from 'mobx'
import { type Storage } from '@/infra/storage'
import { clamp } from '@/utils/math-util'

/**
 * The width range of a {@link PanelWidth}.
 */
export interface PanelWidthBounds {
  defaultWidth: number
  minWidth: number
  maxWidth: number
}

/**
 * The width of a resizable panel, remembered per browser under `storageKey`.
 */
export class PanelWidth {
  /**
   * The width in pixels.
   */
  @observable accessor width: number

  constructor(
    private readonly _storage: Storage,
    private readonly _storageKey: string,
    readonly bounds: PanelWidthBounds,
  ) {
    const stored = Number(_storage.getItem(_storageKey))
    this.width = stored ? this._clamp(stored) : bounds.defaultWidth
  }

  /**
   * Sets the width within the allowed range, without saving it.
   */
  @action.bound
  setWidth(width: number) {
    this.width = this._clamp(width)
  }

  /**
   * Remembers the current width.
   */
  @action.bound
  save() {
    this._storage.setItem(this._storageKey, String(this.width))
  }

  /**
   * Goes back to the default width and forgets the remembered one.
   */
  @action.bound
  reset() {
    this.width = this.bounds.defaultWidth
    this._storage.removeItem(this._storageKey)
  }

  private _clamp(width: number) {
    const { minWidth, maxWidth } = this.bounds
    return Math.round(clamp(width, minWidth, maxWidth))
  }
}
